import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  updateOrganizationPlan,
  activateOrganization,
  deactivateOrganization,
  deleteOrganization,
  getAllOrganizations,
  getSubscriptionStats
} from "../../services/service";
import DataGrid from "../../atoms/DataTable/DataGrid";
import { Spin, Card, Row, Col, Modal, Select, message, Table, Empty, Tabs } from "antd";
import ListHeader from "../../molecules/ListHeader/ListHeader";
import {
  FaUsers,
  FaCalendarAlt,
  FaCrown,
  FaFilter,
  FaSearch,
  FaEnvelope,

  FaCheckCircle,
  FaTimesCircle,
  FaEllipsisH,
  FaArrowRight,
  FaShieldAlt,
  FaCreditCard,
  FaUser
} from "react-icons/fa";
import { Pagination } from "antd";
import "../../assets/css/common/common.scss";
import "./SubscriptionManagement.scss";
import { getOrganizationDisplayPlan } from "../../utils/subscriptionPlanDisplay";

const { Option } = Select;
const SUBSCRIPTION_PLAN_OPTIONS = [
  { value: "price_1TGcrxA1DZhFpfbJ9gkHITBm", label: "Basic - Monthly" },
  { value: "price_1TGcrtA1DZhFpfbJRAw0m1W8", label: "Basic - Yearly" },
  { value: "price_1TGcrnA1DZhFpfbJk7DUzJbg", label: "Premium - Monthly" },
  { value: "price_1TGcroA1DZhFpfbJ2U3ryGXo", label: "Premium - Yearly" },
  { value: "price_1TGcrnA1DZhFpfbJ01zwwMlU", label: "Ultimate - Monthly" },
  { value: "price_1TGcrnA1DZhFpfbJlcjRF0Bm", label: "Ultimate - Yearly" },
  { value: "price_1THlgvA1DZhFpfbJFAzeGQmy", label: "Trial Plan" },
];
const PLAN_CODE_TO_PRICE_ID = {
  BASIC_MONTH: "price_1TGcrxA1DZhFpfbJ9gkHITBm",
  BASIC_YEAR: "price_1TGcrtA1DZhFpfbJRAw0m1W8",
  PREMIUM_MONTH: "price_1TGcrnA1DZhFpfbJk7DUzJbg",
  PREMIUM_YEAR: "price_1TGcroA1DZhFpfbJ2U3ryGXo",
  ULTIMATE_MONTH: "price_1TGcrnA1DZhFpfbJ01zwwMlU",
  ULTIMATE_YEAR: "price_1TGcrnA1DZhFpfbJlcjRF0Bm",
  TRIAL: "price_1THlgvA1DZhFpfbJFAzeGQmy",
};
const PLAN_CODE_TO_LABEL = {
  BASIC_MONTH: "Basic - Monthly",
  BASIC_YEAR: "Basic - Yearly",
  PREMIUM_MONTH: "Premium - Monthly",
  PREMIUM_YEAR: "Premium - Yearly",
  ULTIMATE_MONTH: "Ultimate - Monthly",
  ULTIMATE_YEAR: "Ultimate - Yearly",
  TRIAL: "Trial Plan",
};

const SubscriptionManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [totalAccounts, setTotalAccounts] = useState(0);
  const [totalActiveAccounts, setTotalActiveAccounts] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statsLoading, setStatsLoading] = useState(false);
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [selectedOrgRowId, setSelectedOrgRowId] = useState(null);
  const [selectedOrgDetails, setSelectedOrgDetails] = useState(null);
  const [paymentFilter, setPaymentFilter] = useState("all");

  // Modal states
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchOrganizations = async (page = 1, limit = 10) => {
    setLoading(true);
    try {
      const statusParam =
        statusFilter === "active"
          ? "true"
          : statusFilter === "inactive"
            ? "false"
            : undefined;
      const planParam = planFilter !== "all" ? planFilter : undefined;
      const searchParam = debouncedSearch ? debouncedSearch : undefined;

      const response = await getAllOrganizations(page, limit, {
        is_active: statusParam,
        plan: planParam,
        search: searchParam,
      });

      if (response?.success) {
        setOrganizations(response.data || []);

        if (response.pagination) {
          setPagination({
            current: response.pagination.currentPage,
            pageSize: response.pagination.itemsPerPage,
            total: response.pagination.totalItems,
          });

          setTotalAccounts(response.pagination.totalItems || 0);
          setTotalActiveAccounts(response.pagination?.totalActiveItems || 0);
        }
      }
    } catch (error) {
      console.error("Error fetching subscription data:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSubscriptionOverview();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    fetchOrganizations(1, pagination.pageSize);
  }, [statusFilter, planFilter, debouncedSearch, pagination.pageSize]);

  const handlePageChange = (newPage, newPageSize) => {
    fetchOrganizations(newPage, newPageSize);
  };

  const handleFilterChange = (values = {}) => {
    const status = values?.status || "all";
    const plan = values?.plan || "all";
    setPagination((prev) => ({ ...prev, current: 1 }));
    setStatusFilter(status);
    setPlanFilter(plan);
  };

  const handleSearchInputChange = (value = "") => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSearchTerm(value);
  };

  // Handle plan change modal
  const handleChangePlan = (org) => {
    setSelectedOrg(org);
    const normalizedPlanCode = String(org?.plan_name || "").toUpperCase();
    const normalizedPlanPriceId = PLAN_CODE_TO_PRICE_ID[normalizedPlanCode] || null;
    setSelectedPlan(normalizedPlanPriceId || org.stripe_plan_id || org.plan_id);
    setIsPlanModalOpen(true);
  };

  // Handle plan update
  const handleUpdatePlan = async () => {
    if (!selectedOrg || !selectedPlan) {
      message.error("Please select a plan");
      return;
    }

    // When super admin changes plan, always mark as super_admin change
    const payload = {
      org_id: selectedOrg.org_id,
      plan_id: selectedPlan,
      changed_by_user: false,
      change_source: "super_admin",
    };

    setActionLoading(true);
    try {
      const response = await updateOrganizationPlan(payload);

      if (response?.success) {
        message.success("Organization plan updated successfully");
        setIsPlanModalOpen(false);
        fetchOrganizations(pagination.current, pagination.pageSize);
      } else {
        message.error(response?.message || "Failed to update plan");
      }
    } catch (error) {
      console.error("Error updating plan:", error);
      message.error(error?.message || "Failed to update plan");
    }
    setActionLoading(false);
  };

  // Handle view details
  const handleViewDetails = (org) => {
    setSelectedOrgRowId(org.id);
    setSelectedOrgDetails(org);
    setIsDetailsModalOpen(true);
  };

  // Handle organization status change
  const handleStatusChange = async (org, action) => {
    Modal.confirm({
      centered: true,
      title: `${action === 'activate' ? 'Activate' : 'Deactivate'} Organization`,
      content: `Are you sure you want to ${action} "${org.organization}"?`,
      okText: 'Yes',
      cancelText: 'No',
      okButtonProps: {
        style: {
          backgroundColor: '#F97316',
          borderColor: '#F97316',
          color: 'white',
        },
      },
      onOk: async () => {
        try {
          const apiCall = action === 'activate' ? activateOrganization : deactivateOrganization;
          const response = await apiCall({ org_id: org.org_id });

          if (response?.success) {
            message.success(`Organization ${action}d successfully`);
            fetchOrganizations(pagination.current, pagination.pageSize);
          } else {
            message.error(response?.message || `Failed to ${action} organization`);
          }
        } catch (error) {
          console.error(`Error ${action}ing organization:`, error);
          message.error(error?.message || `Failed to ${action} organization`);
        }
      },
    });
  };

  const handleDeleteOrganization = async (org) => {
    Modal.confirm({
      centered: true,
      title: "Delete Organization",
      content: `Are you sure you want to permanently delete "${org.organization}"? This action cannot be undone.`,
      okText: "Delete",
      cancelText: "Cancel",
      okButtonProps: {
        danger: true,
      },
      onOk: async () => {
        try {
          const organizationId = org?.org_id || org?.id;
          if (!organizationId) {
            message.error("Organization ID not found");
            return;
          }
          const response = await deleteOrganization(organizationId);
          if (response?.success) {
            message.success("Organization deleted successfully");
            fetchOrganizations(pagination.current, pagination.pageSize);
          } else {
            message.error(response?.message || "Failed to delete organization");
          }
        } catch (error) {
          console.error("Error deleting organization:", error);
          message.error(error?.message || "Failed to delete organization");
        }
      },
    });
  };

  const transformOrganization = (m) => {
    const orgUsers = Array.isArray(m?.OrganizationUsers)
      ? m.OrganizationUsers
      : m?.OrganizationUsers
        ? [m.OrganizationUsers]
        : [];
    const primaryUser =
      orgUsers.find((user) =>
        ["manager", "owner", "admin"].includes(
          String(user?.role || "").toLowerCase()
        )
      ) || orgUsers[0] || {};

    const managerName =
      m.manager_name ||
      m.manager?.username ||
      primaryUser?.username ||
      primaryUser?.name ||
      "N/A";
    const email =
      m.email ||
      m.manager_email ||
      primaryUser?.email ||
      "N/A";

    const planChangedByUser =
      typeof m?.plan_changed_by_user === "boolean"
        ? m.plan_changed_by_user
        : typeof m?.planChangeInitiatedByUser === "boolean"
          ? m.planChangeInitiatedByUser
          : null;
    const planChangeSourceRaw =
      m?.plan_change_source ||
      m?.planChangeSource ||
      (planChangedByUser === null
        ? null
        : planChangedByUser
          ? "user"
          : "super_admin");

    // Use the new plan_changed_by field if available, otherwise fall back to existing logic
    const planChangedBy = m?.plan_changed_by ||
      (planChangeSourceRaw === "super_admin" ? "SUPER_ADMIN" :
        planChangeSourceRaw === "user" || planChangeSourceRaw === "manager" ? "Manager" :
          null);

    const planDisplay = getOrganizationDisplayPlan(m, subscriptionStats?.organizations);

    return {
      id: m.id,
      org_id: m.id,
      organization: m.title || primaryUser?.organization?.title || "N/A",
      manager_name: managerName,
      email,
      phoneNo: primaryUser?.phoneNo || "N/A",
      plan: m.OrganizationPlan?.title || "N/A",
      plan_id: m.OrganizationPlan?.id || m.plan_id,
      stripe_plan_id: planDisplay.stripePlanId,
      plan_name: planDisplay.code,
      subscription_end_date: m.subscription_end_date
        ? new Date(m.subscription_end_date).toLocaleDateString()
        : "N/A",
      subscription_status: m.stripe_status
        ? String(m.stripe_status).toUpperCase()
        : "N/A",
      created_at: m.createdAt
        ? new Date(m.createdAt).toLocaleDateString()
        : "N/A",
      is_active: m.is_active,
      recent_active: m.recent_active,
      onChangePlan: handleChangePlan,
      onStatusChange: handleStatusChange,
      onViewDetails: handleViewDetails,
      plan_change_source: planChangeSourceRaw,
      plan_changed_by_user: planChangedByUser,
      plan_changed_by: planChangedBy,
    };
  };

  const transformedData = organizations.map(transformOrganization);

  const displayData = transformedData.filter(org => {
    if (paymentFilter === "all") return true;
    if (paymentFilter === "manual") return org.plan_changed_by === "SUPER_ADMIN";
    if (paymentFilter === "stripe") return org.plan_changed_by !== "SUPER_ADMIN";
    return true;
  });

  const handleActiveFilterToggle = () => {
    if (loading) return;
    setStatusFilter((prev) => (prev === "active" ? "all" : "active"));
  };

  useEffect(() => {
    if (!displayData.length) {
      if (selectedOrgRowId !== null) {
        setSelectedOrgRowId(null);
      }
      if (selectedOrgDetails !== null) {
        setSelectedOrgDetails(null);
      }
      setIsDetailsModalOpen(false);
      return;
    }

    // If a row is selected, make sure the details are synced
    if (selectedOrgRowId) {
      const matchedRow = displayData.find(
        (org) => org.id === selectedOrgRowId
      );
      if (matchedRow && (!selectedOrgDetails || selectedOrgDetails.id !== selectedOrgRowId)) {
        setSelectedOrgDetails(matchedRow);
      } else if (!matchedRow) {
        // Selected row no longer exists in current data
        setSelectedOrgRowId(null);
        setSelectedOrgDetails(null);
        setIsDetailsModalOpen(false);
      }
    }
  }, [displayData, selectedOrgRowId, selectedOrgDetails]);

  useEffect(() => {
    if (location.state?.organizationId && displayData.length > 0) {
      const orgId = location.state.organizationId;
      const org = displayData.find(o => o.org_id === orgId || o.id === orgId);
      if (org && !selectedOrgRowId) {
        handleViewDetails(org);
        // Clear the state so it doesn't re-open on next render/close
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, displayData, selectedOrgRowId, navigate, location.pathname]);

  const handleOrganizationSelectionChange = (newSelectionModel) => {
    const selectionArray = Array.isArray(newSelectionModel)
      ? newSelectionModel
      : [newSelectionModel];
    const latestId = selectionArray.length
      ? selectionArray[selectionArray.length - 1]
      : null;

    if (!latestId) {
      setSelectedOrgRowId(null);
      setSelectedOrgDetails(null);
      setIsDetailsModalOpen(false);
      return;
    }

    const selectedOrg =
      displayData.find((org) => org.id === latestId) ||
      transformedData.find((org) => org.id === latestId);

    setSelectedOrgRowId(latestId);
    setSelectedOrgDetails(selectedOrg || null);
    setIsDetailsModalOpen(true);
  };

  const fetchSubscriptionOverview = async () => {
    setStatsLoading(true);
    try {
      const response = await getSubscriptionStats();
      if (response?.success) {
        const data = response.data || {};
        setSubscriptionStats(data);
      }
    } catch (error) {
      console.error("Error fetching subscription stats:", error);
      message.error(error?.message || "Unable to load subscription statistics");
    }
    setStatsLoading(false);
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
      return "$0.00";
    }
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }).format(Number(value));
    } catch (error) {
      return `$${Number(value).toFixed(2)}`;
    }
  };

  const formatDate = (value) => {
    if (!value) return "N/A";
    try {
      return new Date(value).toLocaleDateString();
    } catch (error) {
      return value;
    }
  };



  const getPlanBadgeClass = (planName) => {
    const normalized = String(planName || "").toUpperCase();
    if (normalized === "ULTIMATE") return "bg-purple-100 text-purple-800";
    if (normalized === "PREMIUM") return "bg-blue-100 text-blue-800";
    if (normalized === "BASIC") return "bg-green-100 text-green-800";
    return "bg-gray-100 text-gray-800";
  };

  const organizationsStats = Array.isArray(subscriptionStats?.organizations)
    ? subscriptionStats.organizations
    : [];

  const selectedStatsOrg = selectedOrgDetails
    ? organizationsStats.find(
      (org) =>
        String(org?.org_id ?? org?.id) ===
        String(selectedOrgDetails?.org_id ?? selectedOrgDetails?.id)
    )
    : null;

  const organizationRevenue = selectedStatsOrg?.total_revenue ?? 0;
  const organizationSubscriptionCount = selectedStatsOrg?.total_purchases ?? 0;

  const organizationLastPurchase =
    selectedStatsOrg?.payments && selectedStatsOrg.payments[0]
      ? selectedStatsOrg.payments[0].created_at
      : null;



  const normalizedPayments = Array.isArray(selectedStatsOrg?.payments)
    ? selectedStatsOrg.payments.map((payment, index) => {
      // Map to new API response format
      const amount = payment?.amount ?? 0;
      const reference = payment?.payment_id ?? `payment-${index}`;
      const purchasedAt = payment?.created_at ?? null;
      const invoiceDate = payment?.invoice_date ? new Date(payment.invoice_date * 1000).toISOString() : null;

      // Generate payment number (First, Second, Third, etc.)
      const paymentNumber = index + 1;
      const getOrdinal = (n) => {
        const s = ["th", "st", "nd", "rd"];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
      };
      const paymentLabel = `${getOrdinal(paymentNumber)} Payment`;

      return {
        key: reference || index,
        paymentNumber: paymentLabel,
        amount,
        purchasedAt,
        invoiceDate,
        reference,
      };
    })
    : [];

  const paymentColumns = [
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (value) => (
        <span className="font-bold text-green-600 text-lg">{formatCurrency(value)}</span>
      ),
    },
    {
      title: "Invoice Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      render: (value) => (
        <span className="font-medium text-gray-700">{formatDate(value)}</span>
      ),
    },
    {
      title: "Payment Date",
      dataIndex: "purchasedAt",
      key: "purchasedAt",
      render: (value) => (
        <span className="text-gray-600">{formatDate(value)}</span>
      ),
    },
  ];

  const isStatusFilterActive = statusFilter === "active";

  return (
    <div className="w-full flex justify-center items-center flex-col gap-4 py-6">
      {/* Header */}
      <div className="w-full px-3 pb-4">
        <ListHeader
          mainHeading="Subscription Management"
          placeholder="Search by organization or manager"
          handleSearchChange={handleFilterChange}
          linkBreadCrum="/"
          blinkBreadCrumText="Super Admin"
          blinkBreadCrumText1="Subscription Management"
          searchShow={false}
          showFilter={false}
          btnShow={false}
        />
      </div>

      {/* Stats Cards - 5 in one line */}
      <div className="w-full px-3 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Total Accounts Created */}
          <div className="w-full">
            <Card
              className="shadow-sm hover:shadow-md transition-all duration-300 h-full"
              style={{ borderRadius: "12px", border: "1px solid #e8e8e8", padding: "12px" }}
              bodyStyle={{ padding: 0 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-[12px] mb-1 font-medium text-nowrap">Total Accounts</p>
                  <h2 className="text-2xl font-bold text-[#1E3A8A] m-0">
                    {loading ? <Spin size="small" /> : totalAccounts}
                  </h2>
                </div>
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#E3F2FD]">
                  <FaUsers style={{ fontSize: "20px", color: "#1E3A8A" }} />
                </div>
              </div>
            </Card>
          </div>

          {/* Active Organizations */}
          <div className="w-full">
            <Card
              hoverable
              onClick={handleActiveFilterToggle}
              className="shadow-sm hover:shadow-md transition-all duration-300 h-full"
              style={{
                borderRadius: "12px",
                border: "1px solid #e8e8e8",
                cursor: loading ? "not-allowed" : "pointer",
                padding: "12px"
              }}
              bodyStyle={{ padding: 0 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-[12px] mb-1 font-medium text-nowrap">Active Subs</p>
                  <h2 className="text-2xl font-bold text-green-600 m-0">
                    {loading ? <Spin size="small" /> : totalActiveAccounts}
                  </h2>
                </div>
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-green-50">
                  <FaCrown style={{ fontSize: "20px", color: "#10B981" }} />
                </div>
              </div>
            </Card>
          </div>

          {/* Renewals */}
          <div className="w-full">
            <Card
              className="shadow-sm hover:shadow-md transition-all duration-300 h-full"
              style={{ borderRadius: "12px", border: "1px solid #e8e8e8", padding: "12px" }}
              bodyStyle={{ padding: 0 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-[12px] mb-1 font-medium text-nowrap">Renewals (30d)</p>
                  <h2 className="text-2xl font-bold text-orange-600 m-0">
                    {loading ? <Spin size="small" /> : organizations.filter((m) => {
                      const endRaw = m?.subscription_end_date;
                      if (!endRaw) return false;
                      const endDate = new Date(endRaw);
                      const today = new Date();
                      const diffDays = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
                      return diffDays <= 30 && diffDays >= 0;
                    }).length}
                  </h2>
                </div>
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-orange-50">
                  <FaCalendarAlt style={{ fontSize: "20px", color: "#EA580C" }} />
                </div>
              </div>
            </Card>
          </div>

          {/* Revenue */}
          <div className="w-full">
            <Card
              className="shadow-sm hover:shadow-md transition-all duration-300 h-full"
              style={{ borderRadius: "12px", border: "1px solid #e8e8e8", padding: "12px" }}
              bodyStyle={{ padding: 0 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-[12px] mb-1 font-medium text-nowrap">Total Revenue</p>
                  <h2 className="text-2xl font-bold text-gray-800 m-0">
                    {statsLoading ? <Spin size="small" /> : formatCurrency(subscriptionStats?.totalRevenue)}
                  </h2>
                </div>
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-50">
                  <span className="text-xl font-bold text-gray-800">$</span>
                </div>
              </div>
            </Card>
          </div>

          {/* Total Subs Sold */}
          <div className="w-full">
            <Card
              className="shadow-sm hover:shadow-md transition-all duration-300 h-full"
              style={{ borderRadius: "12px", border: "1px solid #e8e8e8", padding: "12px" }}
              bodyStyle={{ padding: 0 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-[12px] mb-1 font-medium text-nowrap">Subs Sold</p>
                  <h2 className="text-2xl font-bold text-blue-600 m-0">
                    {statsLoading ? <Spin size="small" /> : (subscriptionStats?.totalSubscriptions ?? 0)}
                  </h2>
                </div>
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <div className="w-full px-3 flex items-center justify-between gap-4 mb-4">
        {/* Left: Payment Filter Buttons */}
        <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-gray-100 shadow-sm h-[48px] shrink-0">
          <button
            onClick={() => setPaymentFilter("all")}
            className={`px-6 h-full rounded-lg text-sm font-semibold transition-all duration-300 flex items-center gap-2 ${paymentFilter === "all"
              ? "bg-[#1E3A8A] text-white shadow-md"
              : "text-gray-500 hover:bg-gray-50"
              }`}
          >
            All Subscriptions
            <span className={`px-2 py-0.5 rounded-full text-[10px] ${paymentFilter === "all" ? "bg-white/20" : "bg-gray-100"
              }`}>
              {transformedData.length}
            </span>
          </button>

          <button
            onClick={() => setPaymentFilter("stripe")}
            className={`px-6 h-full rounded-lg text-sm font-semibold transition-all duration-300 flex items-center gap-2 ${paymentFilter === "stripe"
              ? "bg-[#1E3A8A] text-white shadow-md"
              : "text-gray-500 hover:bg-gray-50"
              }`}
          >
            Stripe
            <span className={`px-2 py-0.5 rounded-full text-[10px] ${paymentFilter === "stripe" ? "bg-white/20" : "bg-gray-100"
              }`}>
              {transformedData.filter(o => o.plan_changed_by !== "SUPER_ADMIN").length}
            </span>
          </button>

          <button
            onClick={() => setPaymentFilter("manual")}
            className={`px-6 h-full rounded-lg text-sm font-semibold transition-all duration-300 flex items-center gap-2 ${paymentFilter === "manual"
              ? "bg-[#1E3A8A] text-white shadow-md"
              : "text-gray-500 hover:bg-gray-50"
              }`}
          >
            Admin
            <span className={`px-2 py-0.5 rounded-full text-[10px] ${paymentFilter === "manual" ? "bg-white/20" : "bg-gray-100"
              }`}>
              {transformedData.filter(o => o.plan_changed_by === "SUPER_ADMIN").length}
            </span>
          </button>
        </div>

        {/* Center: Add Organization Button */}
        <div className="flex-1 flex justify-end pr-6">
          <Link to="/super-admin/add-organization">
            <button className="flex items-center justify-center gap-2 px-10 h-[48px] bg-[#F97316] text-white rounded-lg font-semibold text-sm shadow-md hover:bg-orange-600 transition-all duration-300 hover:scale-[1.05]">
              <i className="fa-solid fa-circle-plus"></i>
              Add Organization
            </button>
          </Link>
        </div>

        {/* Right: Search / ListHeader */}
        <div className="w-[450px] shrink-0">
          <ListHeader
            mainHeading=""
            placeholder="Search organizations..."
            handleSearchChange={handleFilterChange}
            searchShow={true}
            searchValue={searchTerm}
            handleProjectSearchChange={handleSearchInputChange}
            moduleType="super-admin-organizations"
            showFilter={true}
            DropDown={false}
            hideBreadcrumb
          />
        </div>
      </div>

      <div className="w-full px-3 mb-10">
        {loading ? (
          <div className="w-full flex flex-col items-center justify-center py-24 bg-white/50 rounded-2xl border border-dashed border-gray-200">
            <Spin size="large" />
            <p className="mt-4 text-gray-500 font-medium">Crunching organization data...</p>
          </div>
        ) : displayData.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {displayData.map((org) => (
                <div
                  key={org.id}
                  className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-500 flex flex-col overflow-hidden relative"
                >
                  {/* Card Header */}
                  <div className="px-8 pt-7 pb-0">
                    <div className="flex justify-between items-start mb-4">
                      <div className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getPlanBadgeClass(org.plan_name)}`}>
                        {org.plan_name}
                      </div>
                      <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-semibold ${org.is_active ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                        {org.is_active ? <FaCheckCircle size={10} /> : <FaTimesCircle size={10} />}
                        {org.is_active ? 'Active' : 'Inactive'}
                      </div>
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#1E3A8A] transition-colors line-clamp-1 mb-1" title={org.organization}>
                      {org.organization}
                    </h3>
                  </div>

                  {/* Card Body */}
                  <div className="px-8 py-6 bg-gray-50/50 flex-1 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-white border border-gray-100 flex items-center justify-center text-gray-400 shrink-0">
                        <FaUser size={12} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] text-gray-400 font-medium uppercase tracking-tight leading-none mb-1.5">Manager</p>
                        <p className="text-sm font-semibold text-gray-700 truncate">{org.manager_name}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-white border border-gray-100 flex items-center justify-center text-gray-400 shrink-0">
                        <FaEnvelope size={12} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] text-gray-400 font-medium uppercase tracking-tight leading-none mb-1.5">Contact Email</p>
                        <p className="text-sm font-medium text-gray-600 truncate">{org.email}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-white border border-gray-100 flex items-center justify-center text-gray-400 shrink-0">
                        <FaCalendarAlt size={12} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] text-gray-400 font-medium uppercase tracking-tight leading-none mb-1.5">Renewal Date</p>
                        <p className={`text-sm font-semibold ${org.subscription_end_date === 'N/A' ? 'text-gray-400' : 'text-gray-700'}`}>
                          {org.subscription_end_date}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-white border border-gray-100 flex items-center justify-center text-gray-400 shrink-0">
                        {org.plan_changed_by === 'SUPER_ADMIN' ? <FaShieldAlt size={12} className="text-orange-500" /> : <FaCreditCard size={12} className="text-blue-500" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] text-gray-400 font-medium uppercase tracking-tight leading-none mb-1.5">Billing Source</p>
                        <p className={`text-sm font-semibold ${org.plan_changed_by === 'SUPER_ADMIN' ? 'text-orange-700' : 'text-blue-700'}`}>
                          {org.plan_changed_by === 'SUPER_ADMIN' ? 'Admin' : 'Stripe'}
                        </p>
                      </div>
                    </div>

                  </div>

                  {/* Card Actions */}
                  <div className="px-8 pt-4 pb-6 grid grid-cols-2 gap-3 bg-white border-t border-gray-100">
                    <button
                      onClick={() => handleViewDetails(org)}
                      className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs font-bold text-gray-600 bg-gray-50 hover:bg-gray-100 hover:text-gray-900 transition-all border border-transparent hover:border-gray-200"
                    >
                      Details
                    </button>
                    <button
                      onClick={() => handleChangePlan(org)}
                      className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-xs font-bold text-white bg-[#1E3A8A] hover:bg-[#1e3a8aee] shadow-sm hover:shadow-md transition-all active:scale-95"
                    >
                      Edit Plan
                    </button>
                    <button
                      onClick={() => handleStatusChange(org, org.is_active ? 'deactivate' : 'activate')}
                      className={`col-span-2 flex items-center justify-center gap-2 py-3 px-3 rounded-xl text-xs font-bold transition-all border shadow-sm active:scale-95 ${org.is_active
                        ? 'text-red-600 bg-white border-red-100 hover:bg-red-50'
                        : 'text-green-600 bg-white border-green-100 hover:bg-green-50'
                        }`}
                    >
                      {org.is_active ? 'Deactivate Organization' : 'Activate Organization'}
                    </button>
                    <button
                      onClick={() => handleDeleteOrganization(org)}
                      className="col-span-2 flex items-center justify-center gap-2 py-3 px-3 rounded-xl text-xs font-bold transition-all border shadow-sm active:scale-95 text-red-700 bg-red-50 border-red-200 hover:bg-red-100"
                    >
                      Delete Organization
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Premium Pagination */}
            <div className="mt-12 flex justify-center pb-10">
              <div className="bg-white px-6 py-3 rounded-2xl shadow-sm border border-gray-100">
                <Pagination
                  current={pagination.current}
                  total={pagination.total}
                  pageSize={pagination.pageSize}
                  onChange={handlePageChange}
                  showSizeChanger
                  showTotal={(total, range) => (
                    <span className="text-gray-500 font-medium mr-4">
                      Showing <span className="text-[#1E3A8A] font-bold">{range[0]}-{range[1]}</span> of <span className="text-gray-900 font-bold">{total}</span> organizations
                    </span>
                  )}
                  className="custom-pagination"
                />
              </div>
            </div>
          </>
        ) : (
          <div className="w-full flex flex-col items-center justify-center py-32 bg-white rounded-3xl border border-dashed border-gray-200 shadow-inner">
            <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mb-6 text-gray-300">
              <FaSearch size={40} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Organizations Found</h3>
            <p className="text-gray-500 text-center max-w-sm">
              We couldn't find any organizations matching your search criteria or filters.
            </p>
            <button
              onClick={() => {
                setSearchTerm("");
                setPaymentFilter("all");
              }}
              className="mt-8 px-8 py-3 bg-[#1E3A8A] text-white rounded-lg font-bold hover:shadow-xl transition-all active:scale-95"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* Change Plan Modal */}
      <Modal
        title="Change Subscription Plan"
        open={isPlanModalOpen}
        centered
        onOk={handleUpdatePlan}
        onCancel={() => setIsPlanModalOpen(false)}
        confirmLoading={actionLoading}
        okText="Update Plan"
        cancelText="Cancel"
        okButtonProps={{
          style: {
            backgroundColor: '#F97316',
            borderColor: '#F97316',
          },
        }}
      >
        <div className="py-4">
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organization
            </label>
            <p className="text-base font-semibold text-gray-900">{selectedOrg?.organization}</p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Plan
            </label>
            <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${selectedOrg?.plan_id === 6
              ? "bg-green-100 text-green-800"
              : selectedOrg?.plan_id === 7
                ? "bg-blue-100 text-blue-800"
                : "bg-purple-100 text-purple-800"
              }`}>
              {PLAN_CODE_TO_LABEL[String(selectedOrg?.plan_name || "").toUpperCase()] || selectedOrg?.plan}
            </span>
          </div>

          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select New Plan <span className="text-red-500">*</span>
            </label>
            <div className="select-wrapper">
              <Select
                size="large"
                style={{
                  width: '100%',
                }}
                value={selectedPlan}
                onChange={setSelectedPlan}
                placeholder="Select a plan"
                dropdownStyle={{
                  zIndex: 9999,
                }}
              >
                {SUBSCRIPTION_PLAN_OPTIONS.map((plan) => (
                  <Option key={plan.value} value={plan.value}>
                    <span>{plan.label}</span>
                  </Option>
                ))}
              </Select>
            </div>
          </div>
          <div className="mb-2 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-700">
              <strong>Note:</strong> This change will be recorded as a Super Admin override.
              User-initiated plan changes are handled separately through the subscription portal.
            </p>
          </div>
        </div>
      </Modal>

      {/* Organization Details Modal */}
      <Modal
        title={
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-[#1E3A8A] m-0">
              {selectedOrgDetails?.organization || "Organization Details"}
            </h2>
          </div>
        }
        open={isDetailsModalOpen}
        onCancel={() => {
          setIsDetailsModalOpen(false);
          setSelectedOrgRowId(null);
          setSelectedOrgDetails(null);
        }}
        footer={null}
        width={900}
        centered
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: "80vh", overflowY: "auto", overflowX: "hidden" }}
      >
        {selectedOrgDetails ? (
          <div className="py-4">
            {/* Organization Details Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
              <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Manager</p>
                  <p className="text-base font-semibold text-gray-900">
                    {selectedStatsOrg?.manager_name || selectedOrgDetails.manager_name || 'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Email</p>
                  <p className="text-base font-semibold text-gray-900">
                    {selectedStatsOrg?.manager_email || selectedOrgDetails.email || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Plan</p>
                  <p className="text-base font-semibold text-[#1E3A8A]">
                    {selectedOrgDetails.plan_name || 'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Status</p>
                  <span
                    className={`inline-block px-3 py-1 rounded-lg text-sm font-bold ${selectedOrgDetails.recent_active
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-600"
                      }`}
                  >
                    {selectedOrgDetails.recent_active ? "Active" : "Inactive"}
                  </span>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Renewal Date</p>
                  <p className="text-base font-semibold text-gray-900">
                    {selectedOrgDetails.subscription_end_date}
                  </p>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="mb-6">
              {statsLoading ? (
                <div className="w-full flex items-center justify-center py-10">
                  <Spin />
                </div>
              ) : (
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <div className="p-4 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
                      <p className="text-sm text-gray-600 mb-1 font-medium">
                        Total Revenue
                      </p>
                      <p className="text-2xl font-bold text-[#1E3A8A]">
                        {formatCurrency(organizationRevenue)}
                      </p>
                    </div>
                  </Col>
                  <Col xs={24} md={8}>
                    <div className="p-4 rounded-lg bg-gradient-to-br from-green-50 to-green-100 border border-green-200">
                      <p className="text-sm text-gray-600 mb-1 font-medium">
                        Total Purchases
                      </p>
                      <p className="text-2xl font-bold text-green-600">
                        {organizationSubscriptionCount}
                      </p>
                    </div>
                  </Col>
                  <Col xs={24} md={8}>
                    <div className="p-4 rounded-lg bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200">
                      <p className="text-sm text-gray-600 mb-1 font-medium">
                        Last Purchase
                      </p>
                      <p className="text-lg font-bold text-orange-600">
                        {organizationLastPurchase ? formatDate(organizationLastPurchase) : 'N/A'}
                      </p>
                    </div>
                  </Col>
                </Row>
              )}
            </div>

            {/* Payment History Table */}
            <div>
              <h3 className="text-lg font-bold text-[#1E3A8A] mb-4 flex items-center gap-2">
                <span>Payment History</span>
                <span className="text-sm font-normal text-gray-500">
                  ({normalizedPayments.length} transactions)
                </span>
              </h3>
              <Table
                columns={paymentColumns}
                dataSource={normalizedPayments}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} payments`,
                }}
                bordered={false}
                className="subscription-payments-table"
                locale={{
                  emptyText: "No payments recorded for this organization",
                }}
              />
            </div>
          </div>
        ) : (
          <div className="w-full flex flex-col items-center justify-center py-16">
            <Empty
              description="No organization selected"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SubscriptionManagement;
